'use client';

import { motion } from 'motion/react';
import { type ReactNode, useRef, useState } from 'react';
import slugify from 'react-slugify';
import { Container } from '../Container';
import { Typography } from '../Typography';
import { isValidRichText } from '../_lib/isValidRichText';
import { Text } from '../Text';

export type LegalContentsProps = {
	className?: string;
	content: {
		_key?: string;
		heading: string;
		label: string;
		copy: ReactNode;
	}[];
	paddingBottom?: boolean;
};

export function LegalContents(props: LegalContentsProps) {
	const [activeAnchor, setActiveAnchor] = useState<string>();

	const blockOnInViewActiveChange = useRef({
		value: false,
	}).current;

	const sections = props.content.map((section, index: number) => {
		return {
			index: String(index + 1),
			anchor: `${slugify(String(section.heading)).toLowerCase()}-${section._key}-${index + 1}`,
			_key: section._key,
			title: section.heading,
			label: section.label,
			copy: section.copy,
		};
	});

	const handleAnchorClick = (event: MouseEvent, anchor: string) => {
		event.preventDefault();

		const target = document.querySelector(`#${slugify(String(anchor))}`);

		setActiveAnchor(anchor);

		//block active anchor change while scrolling to selected section
		blockOnInViewActiveChange.value = true;
		setTimeout(() => {
			blockOnInViewActiveChange.value = false;
		}, 1000);

		target?.scrollIntoView({ behavior: 'smooth' });
	};
	if (!props.content) {
		return null;
	}

	return (
		<div
			data-component='LegalContents'
			className={`first:mt-(--header-offset) ${props.paddingBottom && 'pb-responsive-5xl'} ${props.className}`}
		>
			<Container>
				<div className={`max-md:mt-8 md:min-h-[100%] md:grid md:grid-cols-[1fr_3fr] md:gap-10`}>
					<div className='h-full max-md:mb-12'>
						<div className='md:h-screen md:overflow-auto md:sticky md:pt-12 md:top-[var(--header-offset,0)]'>
							<Text as='strong' className='text-text-secondary mb-3 block md:mb-4' styleType='label-lg'>
								Contents
							</Text>
							<ul>
								{sections.map(section => (
									<li key={section._key} className={`flex`}>
										<a
											href={`#${section.anchor}`}
											aria-label='Jump to this heading'
											aria-describedby={section.anchor}
											onClick={(event: any) => handleAnchorClick(event, section.anchor)}
											className={`relative text-heading-5xs flex items-center py-2 pl-5 pr-2 font-semibold w-full transition-all duration-slow ease-smooth before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-0.5 before:w-3 before:bg-text-brand before:origin-left before:transition-transform before:duration-slow before:ease-smooth ${activeAnchor === section.anchor ? 'text-text-primary' : 'text-text-secondary opacity-80 pl-0! before:scale-x-0'} hover:opacity-100`}
										>
											{`${section.index}. ${section.label}`}
										</a>
									</li>
								))}
							</ul>
						</div>
					</div>

					<div className={`md:pt-12 lg:pr-[6.75rem]`}>
						{sections.map(section => (
							<section
								className={`w-full after:content-[''] after:bg-border-secondary after:block after:h-[1px] after:w-full after:my-12 last-of-type:after:hidden md:after:my-16`}
								key={section._key}
								id={section.anchor}
							>
								<motion.h3
									className={`text-heading-sm mb-6 md:mb-8`}
									onViewportEnter={() => {
										if (blockOnInViewActiveChange.value) {
											return;
										}
										setActiveAnchor(section.anchor);
									}}
								>
									{`${section.index}. ${section.title}`}
								</motion.h3>
								{isValidRichText(section.copy) && (
									<Typography className='text-text-secondary' styleType='body-lg'>
										{section.copy}
									</Typography>
								)}
							</section>
						))}
					</div>
				</div>
			</Container>
		</div>
	);
}
